import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { UserPlus, RefreshCw, Users, Building2, CreditCard } from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AddUserModal } from "@/components/admin/AddUserModal";
import { EditUserModal } from "@/components/admin/EditUserModal";
import { EditOrganizationModal } from "@/components/admin/EditOrganizationModal";
import { UsersTable } from "@/components/admin/UsersTable";
import { OrganizationTable } from "@/components/admin/OrganizationTable";
import { BillingTable } from "@/components/admin/BillingTable";
import { useAuth } from "@/providers/AuthProvider";
import { useAdminData, User, Organization } from "@/hooks/useAdminData";
import { useUserManagement } from "@/hooks/useUserManagement";
import { useOrganizationManagement } from "@/hooks/useOrganizationManagement";
import { useTranslation } from "react-i18next";

export type { User, Organization };

const AdminConsole = () => {
	const { t } = useTranslation();
	const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
	const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
	const [isEditOrganizationModalOpen, setIsEditOrganizationModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
	const { isSuperAdmin, isAdmin } = useAuth();

	const { users, organizations, isLoading, fetchData } = useAdminData();
	const { handleCreateUser, handleEditUser } = useUserManagement(fetchData);
	const { handleEditCompany } = useOrganizationManagement(fetchData);

	// Modified user creation handler to close modal on success
	const handleUserCreation = async (userData: any) => {
		console.log("handleUserCreation called with:", userData);
		const success = await handleCreateUser(userData);
		console.log("User creation result:", success);
		if (success) {
			setIsAddUserModalOpen(false);
		}
	};

	// Fetch data on component mount
	useEffect(() => {
		console.log("AdminConsole mounted, fetching data...");
		fetchData();
	}, [fetchData]);

	const handleRefresh = () => {
		console.log("Manually refreshing data...");
		fetchData();
	};

	if (!isSuperAdmin && !isAdmin) {
		return (
			<div className="container mx-auto py-8">
				<Card>
					<CardHeader>
						<CardTitle>{t("admin.accessDenied")}</CardTitle>
						<CardDescription>
							{t("admin.permissionMessage")}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	return (
		<div className="container mx-auto py-4">
			<PageHeader
				title={t("admin.console")}
				description={t("admin.description")}
				actions={
					<>
						<Button onClick={handleRefresh} variant="outline" className="mr-2">
							<RefreshCw className="mr-2 h-4 w-4" />
							{t("admin.refresh")}
						</Button>
						<Button onClick={() => setIsAddUserModalOpen(true)} className="bg-digger-blue hover:bg-digger-blue-light">
							<UserPlus className="mr-2 h-4 w-4" />
							{t("admin.addUser")}
						</Button>
					</>
				}
			/>

			<Tabs defaultValue="users" className="mt-6">

				<TabsList>
					<TabsTrigger value="users" className="flex items-center">
						<Users className="mr-2 h-4 w-4" />
						{t("admin.tabs.users")}
					</TabsTrigger>
					<TabsTrigger value="companies" className="flex items-center">
						<Building2 className="mr-2 h-4 w-4" />
						{t("admin.tabs.company")}
					</TabsTrigger>
					{isSuperAdmin && (<TabsTrigger value="billing" className="flex items-center">
						<CreditCard className="mr-2 h-4 w-4" />
						{t("admin.tabs.billing")}
					</TabsTrigger> )}
				</TabsList>

				<TabsContent value="users" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle>{t("admin.users.title")}</CardTitle>
							<CardDescription>
								{t("admin.users.description")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							{isLoading ? (
								<div className="flex justify-center py-8">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
								</div>
							) : (
								<UsersTable
									users={users}
									isLoading={isLoading}
									onEditUser={(user) => {
										setSelectedUser(user);
										setIsEditUserModalOpen(true);
									}}
									onRefresh={handleRefresh}
								/>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="companies" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle>{t("admin.company.title")}</CardTitle>
							<CardDescription>
								{t("admin.company.description")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<OrganizationTable
								organization={organizations}
								isLoading={isLoading}
								onEditCompany={(company) => {
									setSelectedOrganization(company);
									setIsEditOrganizationModalOpen(true);
								}}
							/>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="billing" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle>{t("admin.billing.title")}</CardTitle>
							<CardDescription>
								{t("admin.billing.description")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<BillingTable />
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			<AddUserModal
				isOpen={isAddUserModalOpen}
				onClose={() => setIsAddUserModalOpen(false)}
				onSubmit={handleUserCreation}
				organizations={organizations}
			/>

			{selectedUser && (
				<EditUserModal
					isOpen={isEditUserModalOpen}
					onClose={() => {
						setIsEditUserModalOpen(false);
						setSelectedUser(null);
					}}
					onSubmit={handleEditUser}
					organizations={organizations}
					user={selectedUser}
				/>
			)}

			{selectedOrganization && (
				<EditOrganizationModal
					isOpen={isEditOrganizationModalOpen}
					onClose={() => {
						setIsEditOrganizationModalOpen(false);
						setSelectedOrganization(null);
					}}
					onSubmit={handleEditCompany}
					company={selectedOrganization}
				/>
			)}
		</div>
	);
};

export default AdminConsole;
